import { Button } from "@/components/ui/button";
import { Check, Star, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AnimateOnScroll from "@/components/AnimateOnScroll";

const PricingSection = () => {
  const { t } = useLanguage();
  const plans = [
    {
      name: t('pricing.starter.name'),
      price: "€799",
      period: t('pricing.oneTime'),
      useCase: t('pricing.starter.useCase'),
      features: [
        t('pricing.features.completeLanding'),
        t('pricing.features.advancedSalesStructure'),
        t('pricing.features.contactIntegration'),
        t('pricing.features.aiBanners'),
        t('pricing.features.faq'),
        t('pricing.features.socialIntegration'),
        t('pricing.features.analytics'),
        t('pricing.features.basicAnimations'),
        t('pricing.features.mobileResponsive'),
        t('pricing.features.basicSEO'),
        t('pricing.features.2revisions'),
        t('pricing.features.3dayDelivery')
      ],
      popular: false
    },
    {
      name: t('pricing.business.name'),
      price: "€1,199",
      period: t('pricing.oneTime'),
      useCase: t('pricing.business.useCase'),
      features: [
        t('pricing.features.multiPageWeb'),
        t('pricing.features.3to5Subpages'),
        t('pricing.features.advancedContactForms'),
        t('pricing.features.imageGallery'),
        t('pricing.features.aiBanners'),
        t('pricing.features.faq'),
        t('pricing.features.socialIntegration'),
        t('pricing.features.advancedAnimations'),
        t('pricing.features.analytics'),
        t('pricing.features.blogSection'),
        t('pricing.features.mobileResponsive'),
        t('pricing.features.3revisions'),
        t('pricing.features.5dayDelivery')
      ],
      popular: true
    },
    {
      name: t('pricing.premium.name'),
      price: "€1,899",
      period: t('pricing.oneTime'),
      useCase: t('pricing.premium.useCase'),
      features: [
        t('pricing.features.extensiveWeb'),
        t('pricing.features.5to8Subpages'),
        t('pricing.features.logoAI'),
        t('pricing.features.specialColors'),
        t('pricing.features.imageGallery'),
        t('pricing.features.aiBanners'),
        t('pricing.features.advancedContactForms'),
        t('pricing.features.faq'),
        t('pricing.features.socialIntegration'),
        t('pricing.features.premiumAnimations'),
        t('pricing.features.brandingGuide'),
        t('pricing.features.videoSection'),
        t('pricing.features.prioritySupport'),
        t('pricing.features.5revisions'),
        t('pricing.features.7dayDelivery')
      ],
      popular: false
    }
  ];

  const comparisonFeatures = [
    { key: 'pages', landing: '1', web: '3-5', brand: '5-8' },
    { key: 'aiBanners', landing: true, web: true, brand: true },
    { key: 'contactForm', landing: 'basic', web: 'advanced', brand: 'advanced' },
    { key: 'gallery', landing: false, web: true, brand: true },
    { key: 'faq', landing: true, web: true, brand: true },
    { key: 'social', landing: true, web: true, brand: true },
    { key: 'analytics', landing: true, web: true, brand: true },
    { key: 'blog', landing: false, web: true, brand: true },
    { key: 'aiLogo', landing: false, web: false, brand: true },
    { key: 'brandingGuide', landing: false, web: false, brand: true },
    { key: 'videoSection', landing: false, web: false, brand: true },
    { key: 'prioritySupport', landing: false, web: false, brand: true },
    { key: 'colorScheme', landing: true, web: true, brand: true },
    { key: 'animations', landing: 'animationsBasic', web: 'animationsAdvanced', brand: 'animationsPremium' },
    { key: 'responsive', landing: true, web: true, brand: true },
    { key: 'seo', landing: true, web: true, brand: true },
    { key: 'revisions', landing: '2', web: '3', brand: '5' },
    { key: 'delivery', landing: `3 ${t('pricing.comparison.days')}`, web: `5 ${t('pricing.comparison.days')}`, brand: `7 ${t('pricing.comparison.days')}` },
  ];

  const renderCellValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="w-5 h-5 text-accent mx-auto" />
      ) : (
        <X className="w-5 h-5 text-muted-foreground/50 mx-auto" />
      );
    }
    if (value === 'basic') return t('pricing.comparison.basic');
    if (value === 'advanced') return t('pricing.comparison.advanced');
    if (value === 'animationsBasic') return t('pricing.comparison.animationsBasic');
    if (value === 'animationsAdvanced') return t('pricing.comparison.animationsAdvanced');
    if (value === 'animationsPremium') return t('pricing.comparison.animationsPremium');
    return value;
  };

  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="pricing" className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-outline text-primary mb-4 md:mb-6 uppercase px-2" style={{ fontWeight: 900 }}>
            <span className="relative inline-block">
              <span className="relative z-10">{t('pricing.title')}</span>
              <span 
                className="absolute bottom-2 left-0 right-0 h-4 -rotate-1 -z-0"
                style={{
                  backgroundColor: 'hsl(11, 86%, 55%)',
                  background: 'linear-gradient(104deg, rgba(239, 106, 55, 0) 0.9%, rgba(239, 106, 55, 1) 2.4%, rgba(239, 106, 55, 0.6) 5.8%, rgba(239, 106, 55, 0.1) 93%, rgba(239, 106, 55, 0.8) 96%, rgba(239, 106, 55, 0) 98%), linear-gradient(183deg, rgba(239, 106, 55, 0) 0%, rgba(239, 106, 55, 0.3) 7.9%, rgba(239, 106, 55, 0) 15%)'
                }}
              />
            </span>
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto font-medium text-center mb-4 px-2">
            {t('pricing.subtitle')}
          </p>
          <p className="text-sm text-muted-foreground px-2">
            {t('pricing.description')}
          </p>
          <div className="mt-4 max-w-3xl mx-auto bg-accent/10 border border-accent/20 rounded-lg p-3 md:p-4 mx-2">
            <p className="text-xs md:text-sm font-medium text-accent">
              {t('pricing.additionalNote')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div 
              key={plan.name}
              className={`relative bg-card rounded-2xl p-6 sm:p-8 shadow-elegant border transition-all duration-500 hover:shadow-2xl hover:shadow-accent/50 hover:scale-105 hover:-translate-y-2 ${
                plan.popular 
                  ? 'border-accent shadow-accent ring-2 ring-accent/20 scale-105 sm:scale-100' 
                  : 'border-border/50'
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-accent text-accent-foreground px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium flex items-center">
                    <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">Most Popular</span>
                    <span className="sm:hidden">Popular</span>
                  </div>
                </div>
              )}

              {/* Plan header */}
              <div className="text-center mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-display font-bold text-card-foreground mb-2 uppercase">
                  {plan.name}
                </h3>
                <div className="text-2xl sm:text-3xl font-bold text-accent mb-2">
                  {plan.price}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  {plan.period}
                </div>
                <p className="text-sm font-semibold text-accent bg-accent/10 px-3 py-2 rounded-lg border border-accent/20">
                  {plan.useCase}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-2 sm:space-y-3 lg:space-y-4 mb-4 sm:mb-6 lg:mb-8 px-1 sm:px-0">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-start">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-accent mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-card-foreground leading-relaxed">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button 
                onClick={scrollToContact}
                className={`w-full py-3 sm:py-4 text-sm sm:text-base font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  plan.popular 
                    ? 'bg-accent hover:bg-white text-accent-foreground hover:text-accent border-2 border-accent hover:shadow-accent/30' 
                    : 'bg-primary hover:bg-white text-primary-foreground hover:text-primary border-2 border-primary hover:shadow-primary/30'
                }`}
              >
                {t('pricing.getStarted')}
              </Button>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <AnimateOnScroll variant="up" delay={200}>
          <div className="mt-12 md:mt-16 lg:mt-20 max-w-5xl mx-auto">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center text-foreground mb-8 uppercase">
              {t('pricing.comparison.title')}
            </h3>
            <div className="overflow-x-auto rounded-xl border border-border/50 bg-card shadow-elegant">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-bold text-foreground min-w-[180px]">
                    {t('pricing.comparison.feature')}
                  </TableHead>
                  <TableHead className="text-center font-bold text-foreground min-w-[120px]">
                    {t('pricing.starter.name')}
                    <div className="text-accent text-sm font-normal">€799</div>
                  </TableHead>
                  <TableHead className="text-center font-bold text-accent min-w-[120px] bg-accent/10">
                    {t('pricing.business.name')}
                    <div className="text-accent text-sm font-normal">€1,199</div>
                  </TableHead>
                  <TableHead className="text-center font-bold text-foreground min-w-[120px]">
                    {t('pricing.premium.name')}
                    <div className="text-accent text-sm font-normal">€1,899</div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow 
                    key={feature.key}
                    className={`transition-all duration-200 hover:bg-accent/10 hover:scale-[1.01] cursor-default ${index % 2 === 0 ? 'bg-background/50' : 'bg-muted/20'}`}
                  >
                    <TableCell className="font-medium text-foreground">
                      {t(`pricing.comparison.${feature.key}`)}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {renderCellValue(feature.landing)}
                    </TableCell>
                    <TableCell className="text-center bg-accent/5 text-muted-foreground">
                      {renderCellValue(feature.web)}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {renderCellValue(feature.brand)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Custom note */}
        <div className="text-center mt-8 md:mt-12">
          <p className="text-muted-foreground text-sm md:text-base italic">
            {t('pricing.customNote')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;